import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import {
  createNocType,
  deleteNocType,
  getNocTypes,
  updateNocType,
} from '../services/noc-type.service';
import { GetNocTypesParams, NocTypeInsertInput, NocTypeUpdateInput } from '../types/noc-type.types';

const invalidateNocTypeQueries = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: ['hr-noc-types'] });

export const useGetNocTypes = (params: GetNocTypesParams) => {
  return useGlobalQuery({
    queryKey: ['hr-noc-types', params],
    queryFn: () => getNocTypes(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateNocType = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (input: NocTypeInsertInput) => createNocType(input),
    onSuccess: () => {
      invalidateNocTypeQueries(queryClient);
      toast({ title: t('NOC_TYPE_CREATED') });
    },
  });
};

export const useUpdateNocType = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: { itemId: string; input: NocTypeUpdateInput }) => updateNocType(params),
    onSuccess: () => {
      invalidateNocTypeQueries(queryClient);
      toast({ title: t('NOC_TYPE_UPDATED') });
    },
  });
};

export const useDeleteNocType = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (itemId: string) => deleteNocType(itemId),
    onSuccess: () => {
      invalidateNocTypeQueries(queryClient);
      toast({ title: t('NOC_TYPE_DELETED') });
    },
  });
};
